import YAML from 'yaml';

/**
 * The AsyncAPI specs we publish are produced by a bundler that hoists *every* external `$ref`
 * target into `components/schemas/<parentKey><N>`, using it as a generic scratch namespace
 * regardless of what kind of object was hoisted. That is invalid AsyncAPI: `@asyncapi/parser`
 * rejects the document, and the viewer renders a wall of validation errors instead of the docs.
 *
 * These helpers move the misplaced entries back into the namespace the specification requires.
 * Each rule keys on the *structural position* of the `$ref` (where a channel/message/server is
 * expected) rather than trying to guess what a hoisted object was meant to be.
 *
 * This is a read-side workaround for a producer-side bug. The specs are readonly to us.
 */

const SCHEMAS_PREFIX = '#/components/schemas/';

type UnknownRecord = Record<string, any>;

export type SanitizeAsyncApiStats = {
  /** `$ref`s to non-schema scalars that were inlined at their use site. */
  inlinedScalars: number;
  /** Channels moved out of `components/schemas` into root `channels`. */
  movedChannels: number;
  /** Operation messages repointed at `#/channels/*&#47;messages/*`. */
  movedMessages: number;
  /** Servers moved out of `components/schemas` into root `servers`. */
  movedServers: number;
};

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** AsyncAPI requires every `components/schemas` entry to be an object or a boolean. */
const isSchemaLike = (value: unknown) =>
  typeof value === 'boolean' || isRecord(value);

/** Returns the `components/schemas` key a `$ref` points at, or null if it points elsewhere. */
const hoistedSchemaKey = (ref: unknown) =>
  typeof ref === 'string' && ref.startsWith(SCHEMAS_PREFIX)
    ? ref.slice(SCHEMAS_PREFIX.length)
    : null;

const refOf = (value: unknown) => (isRecord(value) ? value.$ref : undefined);

/**
 * Rule A1 — scalar values are not schemas. Inline them wherever they are referenced, then drop
 * the offending `components/schemas` entries.
 */
const inlineHoistedScalars = (
  document: UnknownRecord,
  schemas: UnknownRecord,
  stats: SanitizeAsyncApiStats,
) => {
  const scalars = new Map<string, unknown>();
  for (const [key, value] of Object.entries(schemas)) {
    if (!isSchemaLike(value)) {
      scalars.set(SCHEMAS_PREFIX + key, value);
    }
  }
  if (scalars.size === 0) {
    return;
  }

  // Mutates in place so that objects shared by several parents are only visited once per parent.
  const inline = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach((value, index) => {
        const ref = refOf(value);
        if (typeof ref === 'string' && scalars.has(ref)) {
          node[index] = scalars.get(ref);
          stats.inlinedScalars++;
        } else {
          inline(value);
        }
      });
      return;
    }
    if (!isRecord(node)) {
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      const ref = refOf(value);
      if (typeof ref === 'string' && scalars.has(ref)) {
        node[key] = scalars.get(ref);
        stats.inlinedScalars++;
      } else {
        inline(value);
      }
    }
  };
  inline(document);

  for (const ref of scalars.keys()) {
    delete schemas[ref.slice(SCHEMAS_PREFIX.length)];
  }
};

/**
 * Rules A2 and A3 — `operations.*.channel` must reference a channel under root `channels`, and
 * `operations.*.messages[]` must reference `#/channels/<channel>/messages/<message>`.
 */
const relocateOperationRefs = (
  document: UnknownRecord,
  schemas: UnknownRecord,
  stats: SanitizeAsyncApiStats,
) => {
  const hadChannels = isRecord(document.channels);
  const channels: UnknownRecord = hadChannels ? document.channels : {};
  document.channels = channels;
  const operations = isRecord(document.operations) ? document.operations : {};

  for (const operation of Object.values(operations)) {
    if (!isRecord(operation)) {
      continue;
    }

    // A2: resolve which root channel this operation belongs to, hoisting it if necessary.
    let channelName: string | null = null;
    const channelRef = refOf(operation.channel);
    const hoistedChannel = hoistedSchemaKey(channelRef);
    if (hoistedChannel !== null && isSchemaLike(schemas[hoistedChannel])) {
      channelName = hoistedChannel;
      if (!channels[channelName]) {
        // Must be an inline copy: a `$ref` back into components/schemas would leave the
        // operation's message refs below unresolvable.
        channels[channelName] = schemas[hoistedChannel];
        stats.movedChannels++;
      }
      operation.channel = { $ref: `#/channels/${channelName}` };
    } else if (
      typeof channelRef === 'string' &&
      channelRef.startsWith('#/channels/')
    ) {
      channelName = channelRef.slice('#/channels/'.length).split('/')[0];
    }
    if (channelName === null) {
      continue;
    }

    const channel = channels[channelName];
    if (!isRecord(channel) || !Array.isArray(operation.messages)) {
      continue;
    }

    // A3: register each message on the channel and repoint the operation at it. Unlike the
    // channel above, a `$ref` into components/schemas is valid in this position.
    operation.messages = operation.messages.map(message => {
      const hoistedMessage = hoistedSchemaKey(refOf(message));
      if (hoistedMessage === null || schemas[hoistedMessage] === undefined) {
        return message;
      }
      const messages: UnknownRecord = isRecord(channel.messages)
        ? channel.messages
        : {};
      channel.messages = messages;
      if (!messages[hoistedMessage]) {
        messages[hoistedMessage] = { $ref: SCHEMAS_PREFIX + hoistedMessage };
        stats.movedMessages++;
      }
      return { $ref: `#/channels/${channelName}/messages/${hoistedMessage}` };
    });
  }

  // Don't introduce an empty `channels` block on specs that never had one.
  if (!hadChannels && Object.keys(channels).length === 0) {
    delete document.channels;
  }
};

/** Rule A4 — `channels.*.servers` must reference a subset of the root `servers`. */
const relocateChannelServers = (
  document: UnknownRecord,
  schemas: UnknownRecord,
  stats: SanitizeAsyncApiStats,
) => {
  const servers: UnknownRecord = isRecord(document.servers)
    ? document.servers
    : {};
  document.servers = servers;
  const channels = isRecord(document.channels) ? document.channels : {};

  for (const channel of Object.values(channels)) {
    if (!isRecord(channel) || !Array.isArray(channel.servers)) {
      continue;
    }
    channel.servers = channel.servers.map(server => {
      const hoistedServer = hoistedSchemaKey(refOf(server));
      if (hoistedServer === null || !isSchemaLike(schemas[hoistedServer])) {
        return server;
      }
      if (!servers[hoistedServer]) {
        servers[hoistedServer] = schemas[hoistedServer];
        stats.movedServers++;
      }
      return { $ref: `#/servers/${hoistedServer}` };
    });
  }

  // Don't leave an empty `servers` block behind on specs that never had one.
  if (Object.keys(servers).length === 0) {
    delete document.servers;
  }
};

/**
 * Moves bundler-hoisted entries out of `components/schemas` and back into the namespace the
 * AsyncAPI specification requires. Mutates `document` in place and reports what it changed.
 */
export const sanitizeAsyncApiDocument = (
  document: UnknownRecord,
): SanitizeAsyncApiStats => {
  const stats: SanitizeAsyncApiStats = {
    inlinedScalars: 0,
    movedChannels: 0,
    movedMessages: 0,
    movedServers: 0,
  };

  const schemas = isRecord(document.components)
    ? document.components.schemas
    : undefined;
  if (!isRecord(schemas)) {
    return stats;
  }

  inlineHoistedScalars(document, schemas, stats);
  relocateOperationRefs(document, schemas, stats);
  relocateChannelServers(document, schemas, stats);

  return stats;
};

/**
 * Sanitizes a raw AsyncAPI definition so `@asyncapi/parser` accepts it.
 *
 * Returns the input untouched when it cannot be parsed, is not an AsyncAPI document, or needs no
 * changes — so a spec that renders today can never be made worse by this function.
 */
export const sanitizeAsyncApiDefinition = (definition: string): string => {
  let document: unknown;
  try {
    document = YAML.parse(definition);
  } catch {
    return definition;
  }
  if (!isRecord(document)) {
    return definition;
  }

  const stats = sanitizeAsyncApiDocument(document);
  const changed = Object.values(stats).some(count => count > 0);
  if (!changed) {
    return definition;
  }

  try {
    return YAML.stringify(document, { lineWidth: 0 });
  } catch {
    return definition;
  }
};
